import React, {ReactNode, useMemo, useState} from 'react';
import { Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {Change, ChangeLog} from "../../../../models/logger/logger.data";
import {Link} from "react-router-dom";
import {InvocationModal} from "../../invocation/modal/invocation.modal";
import {LinkOutlined} from "@ant-design/icons";

interface ChangesTableProps {
    fields: string[];
    changes: ChangeLog[];
    emptyValue?: React.ReactNode;
}

type CellMeta = {
    value: string;
    changed: boolean;
    old?: string;
    new?: string;
};

type RowData = {
    key: string;
    field: string;
    cells: Record<string, CellMeta>;
};

const isSameValue = (a?: string | null, b?: string | null) => {
    return String(a ?? '') === String(b ?? '');
};

export const ChangesTable: React.FC<ChangesTableProps> = ({
  fields,
  changes,
  emptyValue = '—',
}) => {

    const [invocation, setInvocation] = useState<string>('');

    const { columns, dataSource } = useMemo(() => {
        const sortedChanges = [...changes].sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        );

        const currentValues: Record<string, string | undefined> = {};

        for (const log of sortedChanges) {
            for (const change of log.changes) {
                if (currentValues[change.field] === undefined) {
                    currentValues[change.field] = change.old;
                }
            }
        }

        const rowsMap: Record<string, RowData> = Object.fromEntries(
            fields.map((field) => [
                field,
                {
                    key: field,
                    field,
                    cells: {},
                },
            ]),
        );

        sortedChanges.forEach((log, index) => {
            const columnKey = `date_${index}`;
            const changesByField = new Map<string, Change>();

            for (const change of log.changes) {
                if (!isSameValue(change.old, change.new)) {
                    changesByField.set(change.field, change);
                }
            }

            for (const change of log.changes) {
                currentValues[change.field] = change.new;
            }

            for (const field of fields) {
                const change = changesByField.get(field);

                rowsMap[field].cells[columnKey] = {
                    value: currentValues[field] ?? '',
                    changed: Boolean(change),
                    old: change?.old,
                    new: change?.new,
                };
            }
        });

        const tableColumns: ColumnsType<RowData> = [
            {
                title: 'Field',
                dataIndex: 'field',
                key: 'field',
                fixed: 'left',
                width: 280,
                render: (field: string) => <Typography.Text strong>{field}</Typography.Text>,
            },
            ...sortedChanges.map((log, index) => {
                const columnKey = `date_${index}`;
                const date = new Date(log.date);

                return {
                    title: <>
                        <a
                            href={'#'}
                            className="invocation-entity-link"
                            onClick={(event) => {
                                event.preventDefault();
                                setInvocation(log.invocation);
                            }}
                        >
                            <LinkOutlined />
                            {date.toLocaleDateString() + ' ' + date.toLocaleTimeString()}
                        </a>
                    </>,
                    key: columnKey,
                    render: (_: unknown, row: RowData) => {
                        const cell = row.cells[columnKey];

                        if (!cell || cell.value === '') {
                            return emptyValue;
                        }

                        const content = (
                            <span
                                style={
                                    cell.changed
                                        ? {
                                            fontWeight: 600,
                                            background: '#fff7e6',
                                            padding: '2px 6px',
                                            borderRadius: 4,
                                            display: 'inline-block',
                                        }
                                        : undefined
                                }
                            >
                            {cell.value}
                          </span>
                        );

                        if (!cell.changed) {
                            return content;
                        }

                        return (
                            <Tooltip title={`${cell.old ?? ''} → ${cell.new ?? ''}`}>
                                {content}
                            </Tooltip>
                        );
                    },
                };
            }),
        ];

        return {
            columns: tableColumns,
            dataSource: Object.values(rowsMap),
        };
    }, [fields, changes, emptyValue]);

    return (
        <>
            <Table<RowData>
                sticky
                bordered
                size="small"
                pagination={false}
                columns={columns}
                dataSource={dataSource}
                scroll={{ x: 'max-content' }}
            />
            {invocation !== '' ? (
                <InvocationModal invocation={invocation} onClose={() => setInvocation('')} />
            ) : null}
        </>
    );
};